import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { securityApi } from '../services/securityApi';
import type { IntelSource } from '../types/security';

interface SourceDeleteConfirmProps {
  source: IntelSource;
  onClose: () => void;
  onDeleted: () => void;
}

/**
 * WQ-283 (DEF-023) two-step delete confirmation, per WQ-G parked decision #4.
 *
 * Step 1: type-to-confirm source name; Continue enabled only on exact match.
 * Step 2: choose soft vs hard delete; optional rationale; submit.
 */
export function SourceDeleteConfirm({
  source,
  onClose,
  onDeleted,
}: SourceDeleteConfirmProps): React.JSX.Element {
  const { authFetch } = useAuth();
  const [step, setStep] = useState<1 | 2>(1);
  const [typedName, setTypedName] = useState('');
  const [hardDelete, setHardDelete] = useState(false);
  const [rationale, setRationale] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const continueEnabled = typedName === source.name;

  const handleDelete = async () => {
    setError(null);
    setBusy(true);
    try {
      await securityApi.deleteIntelSource(authFetch, source.id, hardDelete, {
        rationale: rationale.trim() ? rationale.trim() : undefined,
      });
      onDeleted();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  // Portal to document.body to escape `.admin-page-content` stacking context
  // (position: relative; z-index: 1 in admin-portal.css). Without portaling,
  // no z-index value can stack above `.admin-header` (z-index: 1000).
  const modalJsx = (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-source-title"
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1002,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{
          background: 'var(--bg-primary, white)',
          borderRadius: '0.5rem',
          padding: '1.25rem',
          width: 'min(480px, 90vw)',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
        }}
      >
        <h3 id="delete-source-title" style={{ marginTop: 0 }}>
          {step === 1 ? 'Confirm source delete' : 'Delete options'}
        </h3>

        {step === 1 && (
          <>
            <p>
              You are about to delete intel source{' '}
              <strong style={{ fontFamily: 'monospace' }}>{source.name}</strong>.
              Type the source name to confirm.
            </p>
            <input
              type="text"
              value={typedName}
              onChange={(e) => setTypedName(e.target.value)}
              placeholder={source.name}
              aria-label="Type source name to confirm"
              style={{ width: '100%', fontFamily: 'monospace' }}
            />
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button
                className="btn btn-primary"
                onClick={() => setStep(2)}
                disabled={!continueEnabled}
                aria-disabled={!continueEnabled}
              >
                Continue
              </button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <input
                type="checkbox"
                checked={hardDelete}
                onChange={(e) => setHardDelete(e.target.checked)}
              />
              <span>
                <strong>Hard-delete</strong> (remove row from database).
                Default is soft-delete (set <code>is_active=false</code> and preserve row).
                Observation history references the source by name; orphan references
                are tolerated.
              </span>
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <strong>Rationale (optional):</strong>
              <textarea
                value={rationale}
                onChange={(e) => setRationale(e.target.value)}
                maxLength={2000}
                rows={3}
                placeholder="Why delete this source?"
              />
            </label>
            {error && (
              <div
                role="alert"
                style={{ marginTop: '0.5rem', color: 'var(--severity-critical-text, #991b1b)' }}
              >
                {error}
              </div>
            )}
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
              <button
                className="btn btn-primary"
                onClick={handleDelete}
                disabled={busy}
                aria-disabled={busy}
              >
                {busy ? 'Deleting…' : hardDelete ? 'Hard-delete' : 'Soft-delete'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );

  return createPortal(modalJsx, document.body);
}
